import React, { useState, useEffect, useCallback } from 'react';
import {
  FiUsers,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiRefreshCw,
  FiUserPlus,
  FiShield,
  FiPhone,
  FiCalendar,
  FiBriefcase,
  FiStar,
  FiAlertTriangle,
  FiCreditCard,
} from 'react-icons/fi';
import AdminLayout from '../components/AdminLayout';
import {
  AdminPageHeader,
  AdminKpiGrid,
  AdminModal,
  AdminEmpty,
  AdminToolbar,
  pillClass,
  roleTone,
  formatDate,
} from '../components/admin';
import { getSubscriptionStatusPresentation, getAdminAppAccessPresentation, getAdminAppAccessMode } from '../utils/subscriptionAccess';
import { FormAlert, FormField, formInputClass } from '../components/form';
import { useApi } from '../hooks/useApi';
import useAdminPermissions from '../hooks/useAdminPermissions';
import { useThemeClasses } from '../hooks/useThemeClasses';
import LoadingSkeleton from '../components/LoadingSkeleton';
import UserAvatar from '../components/UserAvatar';
import AdminUserBillingPanel from '../components/admin/AdminUserBillingPanel';

const EMPTY_FORM = {
  name: '',
  email: '',
  role: 'employee',
  company: '',
  phone: '',
  status: 'active',
  currentPlan: '',
  password: '',
};

const ROLE_LABEL = {
  admin: 'Administrador',
  company_owner: 'Propietario',
  employee: 'Empleado',
  user: 'Usuario',
};

const AdminUsers = () => {
  const tc = useThemeClasses();
  const { secureFetch } = useApi();
  const {
    canManageUsers,
    getUserLimit,
    canAddMoreUsers,
    getABMAccessLevel,
    isSuperAdmin,
  } = useAdminPermissions();

  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [accessFilter, setAccessFilter] = useState('all');
  const [subscriptionFilter, setSubscriptionFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [billingInsights, setBillingInsights] = useState(null);
  const [loadingBilling, setLoadingBilling] = useState(false);

  const accessLevel = getABMAccessLevel();
  const userLimit = getUserLimit();

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await secureFetch('/api/admin/users');
      if (response.ok) {
        setUsers(await response.json());
      } else {
        throw new Error('Error al cargar usuarios');
      }
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
      setErrorMessage('No se pudieron cargar los usuarios.');
    } finally {
      setLoading(false);
    }
  }, [secureFetch]);

  const loadPlans = useCallback(async () => {
    try {
      setLoadingPlans(true);
      const response = await secureFetch('/api/plans');
      if (response.ok) setPlans(await response.json());
    } catch (error) {
      console.error('Error al cargar planes:', error);
    } finally {
      setLoadingPlans(false);
    }
  }, [secureFetch]);

  useEffect(() => {
    if (canManageUsers()) {
      loadUsers();
      loadPlans();
    }
  }, [canManageUsers, loadUsers, loadPlans]);

  useEffect(() => {
    let filtered = users;
    const q = searchTerm.trim().toLowerCase();
    if (q) {
      filtered = filtered.filter((user) =>
        [user.name, user.email, user.company].some((v) => String(v || '').toLowerCase().includes(q))
      );
    }
    if (accessFilter !== 'all') {
      filtered = filtered.filter((user) => {
        if (accessFilter === 'disabled') {
          return String(user.status || '').toLowerCase() === 'suspended';
        }
        return getAdminAppAccessMode(user) === accessFilter;
      });
    }
    if (subscriptionFilter !== 'all') {
      filtered = filtered.filter((user) => {
        const subStatus = String(
          (user.effectiveSubscription || user.subscription)?.status || ''
        ).toLowerCase();
        if (subscriptionFilter === 'past_due') {
          return subStatus === 'past_due' || subStatus === 'grace_readonly';
        }
        return subStatus === subscriptionFilter;
      });
    }
    if (roleFilter !== 'all') filtered = filtered.filter((user) => user.role === roleFilter);
    setFilteredUsers(filtered);
  }, [users, searchTerm, accessFilter, subscriptionFilter, roleFilter]);

  useEffect(() => {
    if (!errorMessage && !successMessage) return undefined;
    const t = setTimeout(() => {
      setErrorMessage('');
      setSuccessMessage('');
    }, 4000);
    return () => clearTimeout(t);
  }, [errorMessage, successMessage]);

  const handleAddUser = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    if (!canAddMoreUsers(users.length)) {
      setErrorMessage('Has alcanzado el límite de usuarios incluido en tu plan.');
      return;
    }
    try {
      const response = await secureFetch('/api/admin/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        setShowAddModal(false);
        setFormData(EMPTY_FORM);
        loadUsers();
        setSuccessMessage('Usuario creado correctamente.');
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Error al crear usuario');
      }
    } catch (error) {
      setErrorMessage(error.message || 'Error al crear el usuario.');
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      const response = await secureFetch(`/api/admin/users/${selectedUser._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        setShowEditModal(false);
        setSelectedUser(null);
        setFormData(EMPTY_FORM);
        loadUsers();
        setSuccessMessage('Usuario actualizado correctamente.');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al actualizar usuario');
      }
    } catch (error) {
      setErrorMessage(error.message || 'Error al actualizar el usuario.');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('¿Eliminar este usuario? Esta acción no se puede deshacer.')) return;
    try {
      const response = await secureFetch(`/api/admin/users/${userId}`, { method: 'DELETE' });
      if (response.ok) {
        loadUsers();
        setSuccessMessage('Usuario eliminado correctamente.');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al eliminar usuario');
      }
    } catch (error) {
      setErrorMessage(error.message || 'Error al eliminar el usuario.');
    }
  };

  const handlePurgeRetention = async (user) => {
    const status = String(user.status || '').toLowerCase();
    const subStatus = String(user.subscription?.status || status).toLowerCase();
    if (!['suspended', 'canceled'].includes(subStatus)) {
      setErrorMessage('Solo se puede purgar cuentas suspendidas o canceladas.');
      return;
    }
    if (
      !window.confirm(
        `¿Purgar la cuenta de ${user.email}? Se anonimizará el email y el estado pasará a "purged". No se puede deshacer.`
      )
    ) {
      return;
    }
    try {
      const response = await secureFetch(`/api/admin/users/${user._id}/purge-retention`, { method: 'POST' });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        loadUsers();
        setSuccessMessage(data.message || 'Cuenta purgada correctamente.');
      } else {
        throw new Error(data.message || 'Error al purgar la cuenta');
      }
    } catch (error) {
      setErrorMessage(error.message || 'Error al purgar la cuenta.');
    }
  };

  const openAddModal = () => {
    setFormData(EMPTY_FORM);
    setShowAddModal(true);
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      role: user.role || 'employee',
      company: user.company || '',
      phone: user.phone || '',
      status: user.status || 'active',
      currentPlan: user.currentPlan?._id || '',
      password: '',
    });
    setShowEditModal(true);
  };

  const openViewModal = async (user) => {
    setSelectedUser(user);
    setBillingInsights(null);
    setShowViewModal(true);
    if (!isSuperAdmin()) return;
    setLoadingBilling(true);
    try {
      const response = await secureFetch(`/api/admin/users/${user._id}/subscription-insights`);
      if (response.ok) {
        setBillingInsights(await response.json());
      }
    } catch (err) {
      console.error('Error al cargar suscripción admin:', err);
    } finally {
      setLoadingBilling(false);
    }
  };

  const RoleBadge = ({ role }) => (
    <span className={pillClass(tc.modoOscuro, roleTone(role))}>
      {ROLE_LABEL[role] || ROLE_LABEL.user}
    </span>
  );

  const AccessBadge = ({ user }) => {
    const pres = getAdminAppAccessPresentation(user);
    const tone = pres.tone === 'muted' ? 'neutral' : pres.tone;
    return (
      <span className={pillClass(tc.modoOscuro, tone)} title="Según suscripción MP (o empresa del titular)">
        {pres.label}
      </span>
    );
  };

  const PlanBadge = ({ plan }) => (
    <span className={pillClass(tc.modoOscuro, plan?.name ? 'accent' : 'neutral')}>
      {plan?.name || 'Sin plan'}
    </span>
  );

  const SubscriptionBadge = ({ user }) => {
    const viewUser = user.effectiveSubscription
      ? { ...user, subscription: user.effectiveSubscription, companyBillingSubscription: user.effectiveSubscription }
      : user;
    const pres = getSubscriptionStatusPresentation(viewUser);
    const tone = pres.tone === 'muted' ? 'neutral' : pres.tone;
    const title = user.subscriptionBillingOwnerEmail
      ? `Facturación de ${user.subscriptionBillingCompany || 'la empresa'} (${user.subscriptionBillingOwnerEmail})`
      : undefined;
    return (
      <span className={pillClass(tc.modoOscuro, tone)} title={title}>
        {pres.label}
      </span>
    );
  };

  const kpis = [
    { icon: FiUsers, label: 'Usuarios', value: users.length, hint: `Límite del plan: ${userLimit}` },
    {
      icon: FiShield,
      label: 'Pueden operar',
      value: users.filter((u) => getAdminAppAccessMode(u) === 'full').length,
      hint: 'Acceso completo a la app',
    },
    {
      icon: FiStar,
      label: 'Suscripción OK',
      value: users.filter((u) => {
        const sub = u.effectiveSubscription || u.subscription;
        const s = String(sub?.status || '').toLowerCase();
        return ['active', 'trialing', 'authorized'].includes(s);
      }).length,
      hint: 'Pago MP activo o en prueba',
    },
    { icon: FiBriefcase, label: 'Admins', value: users.filter((u) => u.role === 'admin' || u.role === 'company_owner').length, hint: 'Roles de gestión' },
  ];

  const ActionButtons = ({ user }) => {
    const subStatus = String(user.subscription?.status || user.status || '').toLowerCase();
    const canPurge = isSuperAdmin() && ['suspended', 'canceled'].includes(subStatus);
    return (
      <div className="inline-flex items-center gap-1.5">
        <button type="button" onClick={() => openViewModal(user)} className={`p-1.5 rounded-md border ${tc.btnOutline}`} title="Ver">
          <FiEye className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => openEditModal(user)} className={`p-1.5 rounded-md border ${tc.btnOutline}`} title="Editar">
          <FiEdit2 className="w-4 h-4" />
        </button>
        {canPurge && (
          <button
            type="button"
            onClick={() => handlePurgeRetention(user)}
            className={`p-1.5 rounded-md border border-red-300 text-red-600 dark:border-red-800 dark:text-red-400 ${tc.modoOscuro ? 'hover:bg-red-950/40' : 'hover:bg-red-50'}`}
            title="Purgar retención (irreversible)"
          >
            <FiAlertTriangle className="w-4 h-4" />
          </button>
        )}
        <button type="button" onClick={() => handleDeleteUser(user._id)} className={`p-1.5 rounded-md border ${tc.btnOutline}`} title="Eliminar">
          <FiTrash2 className="w-4 h-4" />
        </button>
      </div>
    );
  };

  const RoleSelect = () => (
    <select
      value={formData.role}
      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
      className={formInputClass(tc.inputClass, { compact: true })}
    >
      <option value="employee">Empleado</option>
      <option value="user">Usuario</option>
      {accessLevel === 'full' && (
        <>
          <option value="admin">Administrador</option>
          <option value="company_owner">Propietario de empresa</option>
        </>
      )}
    </select>
  );

  const PlanSelect = () => (
    <select
      value={formData.currentPlan}
      onChange={(e) => setFormData({ ...formData, currentPlan: e.target.value })}
      className={formInputClass(tc.inputClass, { compact: true })}
      disabled={loadingPlans}
    >
      <option value="">{loadingPlans ? 'Cargando planes…' : 'Seleccionar plan'}</option>
      {plans.map((plan) => (
        <option key={plan._id} value={plan._id}>
          {plan.name} — ${plan.price}
        </option>
      ))}
    </select>
  );

  if (!canManageUsers()) {
    return (
      <AdminLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <AdminEmpty
            icon={FiShield}
            title="Acceso restringido"
            subtitle="Tu usuario no tiene permisos para ver la gestión de usuarios."
          />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout requiredPermission={canManageUsers}>
      <div className="max-w-7xl mx-auto py-6 space-y-5">
        <AdminPageHeader
          title="Usuarios"
          subtitle="Personas con acceso a Contsuite y sus roles."
          actions={
            <>
              <span className={`text-xs ${tc.textSec}`}>
                {users.length} / {userLimit}
              </span>
              <button type="button" onClick={loadUsers} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs ${tc.btnOutline}`}>
                <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                Actualizar
              </button>
              {canAddMoreUsers(users.length) && (
                <button type="button" onClick={openAddModal} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs ${tc.btnPrimary}`}>
                  <FiUserPlus className="w-4 h-4" />
                  Añadir
                </button>
              )}
            </>
          }
        />

        <AdminKpiGrid items={kpis} />

        {errorMessage && <FormAlert type="error">{errorMessage}</FormAlert>}
        {successMessage && <FormAlert type="success">{successMessage}</FormAlert>}

        <AdminToolbar
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Buscar por nombre, email o empresa…"
          meta={`${filteredUsers.length} usuario(s) · límite del plan ${userLimit}`}
          filters={
            <>
              <select
                value={accessFilter}
                onChange={(e) => setAccessFilter(e.target.value)}
                className={formInputClass(tc.inputClass, { compact: true }) + ' lg:w-44'}
              >
                <option value="all">Todo acceso</option>
                <option value="full">Puede operar</option>
                <option value="read_only">Solo lectura</option>
                <option value="blocked">Sin acceso</option>
                <option value="disabled">Deshabilitado (ABM)</option>
              </select>
              <select
                value={subscriptionFilter}
                onChange={(e) => setSubscriptionFilter(e.target.value)}
                className={formInputClass(tc.inputClass, { compact: true }) + ' lg:w-48'}
              >
                <option value="all">Todas las suscripciones</option>
                <option value="active">Suscripción activa</option>
                <option value="trialing">En prueba</option>
                <option value="pending_payment">Pago pendiente</option>
                <option value="past_due">Gracia / mora</option>
                <option value="suspended">Suspendida</option>
                <option value="canceled">Cancelada</option>
              </select>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className={formInputClass(tc.inputClass, { compact: true }) + ' lg:w-44'}
              >
                <option value="all">Todos los roles</option>
                <option value="admin">Administrador</option>
                <option value="company_owner">Propietario</option>
                <option value="employee">Empleado</option>
                <option value="user">Usuario</option>
              </select>
            </>
          }
        />

        <div className="space-y-3 md:hidden">
          {loading ? (
            <div className={`rounded-2xl border px-4 py-8 text-center text-sm ${tc.box} ${tc.textSec}`}>Cargando…</div>
          ) : filteredUsers.length === 0 ? (
            <div className={`rounded-2xl border ${tc.box}`}>
              <AdminEmpty icon={FiUsers} title="No se encontraron usuarios" subtitle="Probá otro filtro o creá un usuario nuevo." />
            </div>
          ) : (
            filteredUsers.map((user) => (
              <div key={user._id} className={`rounded-2xl border px-4 py-3 flex items-start justify-between gap-3 ${tc.box}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-3">
                    <UserAvatar user={user} size="sm" />
                    <div className="min-w-0">
                      <div className={`text-sm font-medium truncate ${tc.textPri}`}>{user.name}</div>
                      <div className={`text-xs truncate ${tc.textMuted}`}>{user.email}</div>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <RoleBadge role={user.role} />
                    <PlanBadge plan={user.currentPlan} />
                    <SubscriptionBadge user={user} />
                    <AccessBadge user={user} />
                  </div>
                </div>
                <ActionButtons user={user} />
              </div>
            ))
          )}
        </div>

        <div className={`hidden md:block rounded-2xl border overflow-hidden ${tc.box}`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className={`text-[11px] uppercase tracking-wide ${tc.modoOscuro ? 'bg-gray-800 text-gray-300' : 'bg-gray-50 text-gray-500'}`}>
                <tr>
                  <th className="px-4 py-2.5 text-left font-medium">Usuario</th>
                  <th className="px-4 py-2.5 text-left font-medium">Rol</th>
                  <th className="px-4 py-2.5 text-left font-medium">Empresa</th>
                  <th className="px-4 py-2.5 text-left font-medium">Plan</th>
                  <th className="px-4 py-2.5 text-left font-medium">Suscripción</th>
                  <th className="px-4 py-2.5 text-left font-medium">Acceso app</th>
                  <th className="px-4 py-2.5 text-left font-medium">Último acceso</th>
                  <th className="px-4 py-2.5 text-right font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${tc.modoOscuro ? 'divide-gray-800' : 'divide-gray-100'}`}>
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <tr key={`skeleton-${index}`}>
                      <td colSpan="8" className="px-6 py-4">
                        <LoadingSkeleton type="tableRow" />
                      </td>
                    </tr>
                  ))
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="8">
                      <AdminEmpty icon={FiUsers} title="No se encontraron usuarios" subtitle="Probá otro filtro o creá un usuario nuevo." />
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user._id} className={tc.modoOscuro ? 'hover:bg-gray-800/70' : 'hover:bg-gray-50'}>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <UserAvatar user={user} size="sm" />
                          <div>
                            <div className={`text-sm font-medium ${tc.textPri}`}>{user.name}</div>
                            <div className={`text-xs ${tc.textMuted}`}>{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap"><RoleBadge role={user.role} /></td>
                      <td className={`px-4 py-3 whitespace-nowrap text-xs ${tc.textPri}`}>{user.company || '—'}</td>
                      <td className="px-4 py-3 whitespace-nowrap"><PlanBadge plan={user.currentPlan} /></td>
                      <td className="px-4 py-3 whitespace-nowrap"><SubscriptionBadge user={user} /></td>
                      <td className="px-4 py-3 whitespace-nowrap"><AccessBadge user={user} /></td>
                      <td className={`px-4 py-3 whitespace-nowrap text-xs ${tc.textMuted}`}>{formatDate(user.lastLogin, 'Nunca')}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-right"><ActionButtons user={user} /></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <AdminModal open={showAddModal} title="Nuevo usuario" onClose={() => setShowAddModal(false)}>
          <form onSubmit={handleAddUser} className="space-y-3">
            <FormField label="Nombre" required labelClass={tc.labelClass}>
              <input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className={formInputClass(tc.inputClass, { compact: true })} />
            </FormField>
            <FormField label="Email" required labelClass={tc.labelClass}>
              <input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className={formInputClass(tc.inputClass, { compact: true })} />
            </FormField>
            <FormField label="Rol" labelClass={tc.labelClass}><RoleSelect /></FormField>
            <FormField label="Empresa" labelClass={tc.labelClass}>
              <input value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} className={formInputClass(tc.inputClass, { compact: true })} />
            </FormField>
            <FormField label="Teléfono" labelClass={tc.labelClass}>
              <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className={formInputClass(tc.inputClass, { compact: true })} />
            </FormField>
            <FormField label="Plan" labelClass={tc.labelClass}><PlanSelect /></FormField>
            <FormField label="Contraseña" required labelClass={tc.labelClass}>
              <input type="password" required autoComplete="new-password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className={formInputClass(tc.inputClass, { compact: true })} />
            </FormField>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => setShowAddModal(false)} className={`flex-1 px-4 py-2 rounded-lg border text-sm ${tc.btnOutline}`}>Cancelar</button>
              <button type="submit" className={`flex-1 px-4 py-2 rounded-lg text-sm ${tc.btnPrimary}`}>Crear usuario</button>
            </div>
          </form>
        </AdminModal>

        <AdminModal open={showEditModal && !!selectedUser} title="Editar usuario" onClose={() => setShowEditModal(false)}>
          <form onSubmit={handleEditUser} className="space-y-3">
            <FormField label="Nombre" required labelClass={tc.labelClass}>
              <input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className={formInputClass(tc.inputClass, { compact: true })} />
            </FormField>
            <FormField label="Email" required labelClass={tc.labelClass}>
              <input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className={formInputClass(tc.inputClass, { compact: true })} />
            </FormField>
            <FormField label="Rol" labelClass={tc.labelClass}><RoleSelect /></FormField>
            <FormField label="Plan" labelClass={tc.labelClass}><PlanSelect /></FormField>
            <FormField label="Estado" labelClass={tc.labelClass}>
              <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className={formInputClass(tc.inputClass, { compact: true })}>
                <option value="active">Activo</option>
                <option value="inactive">Inactivo</option>
                <option value="suspended">Suspendido</option>
                <option value="pending">Pendiente</option>
              </select>
            </FormField>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => setShowEditModal(false)} className={`flex-1 px-4 py-2 rounded-lg border text-sm ${tc.btnOutline}`}>Cancelar</button>
              <button type="submit" className={`flex-1 px-4 py-2 rounded-lg text-sm ${tc.btnPrimary}`}>Guardar cambios</button>
            </div>
          </form>
        </AdminModal>

        <AdminModal
          open={showViewModal && !!selectedUser}
          title="Detalle del usuario"
          onClose={() => setShowViewModal(false)}
          footer={
            <button type="button" onClick={() => setShowViewModal(false)} className={`w-full px-4 py-2 rounded-lg text-sm ${tc.btnPrimary}`}>
              Cerrar
            </button>
          }
        >
          {selectedUser && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <UserAvatar user={selectedUser} size="md" />
                <div>
                  <h4 className={`text-sm font-semibold ${tc.textPri}`}>{selectedUser.name}</h4>
                  <p className={`text-xs ${tc.textSec}`}>{selectedUser.email}</p>
                </div>
              </div>
              <div className="space-y-2.5">
                <div className="flex items-center gap-2">
                  <FiShield className={`w-4 h-4 ${tc.textMuted}`} />
                  <span className={`text-xs ${tc.textSec}`}>Rol</span>
                  <RoleBadge role={selectedUser.role} />
                </div>
                <div className="flex items-center gap-2">
                  <FiBriefcase className={`w-4 h-4 ${tc.textMuted}`} />
                  <span className={`text-xs ${tc.textSec}`}>Empresa</span>
                  <span className={`text-xs ${tc.textPri}`}>{selectedUser.company || '—'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <FiStar className={`w-4 h-4 ${tc.textMuted}`} />
                  <span className={`text-xs ${tc.textSec}`}>Plan</span>
                  <PlanBadge plan={selectedUser.currentPlan} />
                </div>
                <div className="flex items-center gap-2">
                  <FiCreditCard className={`w-4 h-4 ${tc.textMuted}`} />
                  <span className={`text-xs ${tc.textSec}`}>Suscripción (pago MP)</span>
                  <SubscriptionBadge user={selectedUser} />
                </div>
                <div className="flex items-center gap-2">
                  <FiShield className={`w-4 h-4 ${tc.textMuted}`} />
                  <span className={`text-xs ${tc.textSec}`}>Acceso a la app</span>
                  <AccessBadge user={selectedUser} />
                </div>
                {isSuperAdmin() && (
                  <AdminUserBillingPanel insights={billingInsights} loading={loadingBilling} />
                )}
                <div className="flex items-center gap-2">
                  <FiPhone className={`w-4 h-4 ${tc.textMuted}`} />
                  <span className={`text-xs ${tc.textSec}`}>Teléfono</span>
                  <span className={`text-xs ${tc.textPri}`}>{selectedUser.phone || '—'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <FiCalendar className={`w-4 h-4 ${tc.textMuted}`} />
                  <span className={`text-xs ${tc.textSec}`}>Último acceso</span>
                  <span className={`text-xs ${tc.textPri}`}>{formatDate(selectedUser.lastLogin, 'Nunca')}</span>
                </div>
                {String(selectedUser.status || '').toLowerCase() === 'inactive' && getAdminAppAccessMode(selectedUser) === 'full' && (
                  <p className={`text-[11px] ${tc.textMuted}`}>
                    El campo ABM «inactivo» no bloquea el login; manda el estado de suscripción en Mercado Pago.
                  </p>
                )}
              </div>
            </div>
          )}
        </AdminModal>
      </div>
    </AdminLayout>
  );
};

export default AdminUsers;
